import { createContext, type FC, type ReactNode, useContext, useMemo, useState } from 'react'
import { memo } from 'react'
import { useLocation } from 'react-router-dom'

import { abilitiesFor } from '@app/shared/authorization/caslProvider'
import type { MapMode, StrategyQueryStrategy } from '@app/types'
import type { User as UserType } from '@graphql/types'

interface Props {
  canEdit: boolean
  mapMode: MapMode
  setMapMode: (mode: MapMode) => void
  children: ReactNode
}

type User = Pick<UserType, 'abilities'> & {
  organization?: Pick<UserType['organization'], 'id'>
}

interface PermissionsContextWrapperProps {
  strategy: StrategyQueryStrategy
  user?: User | null
  children: ReactNode
}

const StrategyMapContext = createContext(undefined)

const StrategyMapContextProvider: FC<Props> = memo(({ canEdit, mapMode, setMapMode, children }) => {
  const value = useMemo(
    () => ({
      canEdit,
      mapMode,
      setMapMode
    }),
    [canEdit, mapMode, setMapMode]
  )

  return <StrategyMapContext.Provider value={value}>{children}</StrategyMapContext.Provider>
})

const StrategyMapContextWrapper: FC<PermissionsContextWrapperProps> = ({ strategy, user = null, children }) => {
  const location = useLocation()
  const isReportPage = ['dashboard', 'key-results', 'changelog'].some((path) => location.pathname.includes(path))
  const [mapMode, setMapMode] = useState<MapMode>(isReportPage ? 'reports' : 'build')

  const userInStrategyOrganization = user && user.organization?.id === strategy.organization.id
  const isAnonymousUser = user === null || !userInStrategyOrganization

  const canEdit =
    !isAnonymousUser && abilitiesFor(user).can('update', 'strategy') && !strategy.backgroundChangeInProgress

  return (
    <StrategyMapContextProvider canEdit={canEdit} mapMode={mapMode} setMapMode={setMapMode}>
      {children}
    </StrategyMapContextProvider>
  )
}

const useStrategyMapContext = () => {
  const context = useContext<Props>(StrategyMapContext)

  if (context === undefined) {
    throw new Error('useStrategyMapContext must be used within StrategyMapContext.Provider')
  }

  return context
}

export { StrategyMapContextProvider, useStrategyMapContext }

export default StrategyMapContextWrapper
